import { supabase } from '@/core/supabase/client';
import type { MobilityMode } from '@/features/clients/clients';

/** Todo honorario se expresa en KG DE NOVILLO. No existe cobro directo en pesos. */
export type Calculation = 'per_unit' | 'per_job';

export interface OrganizationService {
  id: string;
  organization_id: string;
  catalog_id: string | null;
  name: string;
  kg_novillo: number;
  calculation: Calculation;
  is_active: boolean;
  sort_order: number;
}

export type PriceKind = 'steer' | 'fuel';

export interface ReferencePrice {
  id: string;
  organization_id: string;
  kind: PriceKind;
  period_year: number;
  period_month: number;
  amount_cents: number;
  created_at: string;
}

export const CALCULATION_LABELS: Record<Calculation, string> = {
  per_unit: 'Por animal / unidad',
  per_job: 'Fijo por trabajo',
};

export const PRICE_LABELS: Record<PriceKind, string> = {
  steer: 'Precio del novillo (por kg)',
  fuel: 'Precio del combustible (por km)',
};

export const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

// ---------------------------------------------------------------------------
// Tarifario
// ---------------------------------------------------------------------------

/** Todos los honorarios, activos e inactivos, para la pantalla de configuración. */
export async function listServices(organizationId: string): Promise<OrganizationService[]> {
  const { data, error } = await supabase
    .from('organization_services')
    .select('*')
    .eq('organization_id', organizationId)
    .order('is_active', { ascending: false })
    .order('sort_order')
    .order('name');
  if (error) throw error;
  return (data ?? []) as OrganizationService[];
}

/** Solo los activos: lo que se ofrece al cargar un trabajo nuevo. */
export async function listActiveServices(organizationId: string): Promise<OrganizationService[]> {
  const { data, error } = await supabase
    .from('organization_services')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .order('sort_order')
    .order('name');
  if (error) throw error;
  return (data ?? []) as OrganizationService[];
}

export async function createService(input: {
  organizationId: string;
  name: string;
  kgNovillo: number;
  calculation: Calculation;
}): Promise<OrganizationService> {
  const { data, error } = await supabase
    .from('organization_services')
    .insert({
      organization_id: input.organizationId,
      name: input.name.trim(),
      kg_novillo: input.kgNovillo,
      calculation: input.calculation,
      sort_order: 999,
    })
    .select()
    .single();
  if (error) throw error;
  return data as OrganizationService;
}

export async function updateService(
  id: string,
  patch: Partial<Pick<OrganizationService, 'name' | 'kg_novillo' | 'calculation' | 'is_active'>>,
): Promise<void> {
  const { error } = await supabase.from('organization_services').update(patch).eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Precios de referencia
// ---------------------------------------------------------------------------

/** Historial completo, más reciente primero. Modificar hoy no toca el pasado. */
export async function listPrices(
  organizationId: string,
  kind: PriceKind,
): Promise<ReferencePrice[]> {
  const { data, error } = await supabase
    .from('reference_prices')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('kind', kind)
    .order('period_year', { ascending: false })
    .order('period_month', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ReferencePrice[];
}

/**
 * Precio de un período, o `null` si no está cargado.
 * `null` significa PENDIENTE: nunca se traduce a 0.
 */
export async function resolvePrice(
  organizationId: string,
  kind: PriceKind,
  year: number,
  month: number,
): Promise<number | null> {
  const { data, error } = await supabase.rpc('resolve_reference_price', {
    p_org: organizationId,
    p_kind: kind,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return (data as number | null) ?? null;
}

export async function upsertPrice(input: {
  organizationId: string;
  kind: PriceKind;
  year: number;
  month: number;
  amountCents: number;
}): Promise<void> {
  const { error } = await supabase.from('reference_prices').upsert(
    {
      organization_id: input.organizationId,
      kind: input.kind,
      period_year: input.year,
      period_month: input.month,
      amount_cents: input.amountCents,
    },
    { onConflict: 'organization_id,kind,period_year,period_month' },
  );
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Movilidad de la organización
// ---------------------------------------------------------------------------

export interface MobilitySettings {
  organization_id: string;
  default_mobility_mode: MobilityMode;
  default_mobility_rate_cents: number | null;
  default_flat_mobility_cents: number | null;
  charge_mobility_by_default: boolean;
}

export async function updateMobilitySettings(
  organizationId: string,
  patch: Partial<Omit<MobilitySettings, 'organization_id'>>,
): Promise<void> {
  const { error } = await supabase
    .from('organization_settings')
    .update(patch)
    .eq('organization_id', organizationId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Formato
// ---------------------------------------------------------------------------

const money = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

/**
 * Con centavos. La app redondea a pesos porque los importes son grandes y el
 * centavo no aporta, pero hay un lugar donde sí: la conciliación de la
 * migración, donde una diferencia de 19 centavos mostrada como "$ 0"
 * contradice el dato que la pantalla existe para mostrar.
 */
const moneyExacto = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatCentsExact(cents: number | null): string {
  if (cents === null) return 'Pendiente';
  return moneyExacto.format(cents / 100);
}

/** Formatea centavos. `null` NO es 0: es "pendiente" y se muestra como tal. */
export function formatCents(cents: number | null): string {
  if (cents === null) return 'Pendiente';
  return money.format(cents / 100);
}

export function parsePesosToCents(input: string): number | null {
  const clean = input.replace(/\./g, '').replace(',', '.').trim();
  if (clean === '') return null;
  const value = Number(clean);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

/**
 * Borrar un honorario del tarifario.
 *
 * Los trabajos viejos no se tocan: cada línea guardó el nombre, los kg y el
 * tipo de cálculo que el honorario tenía el día que se cargó, así que siguen
 * valiendo exactamente lo mismo aunque el honorario ya no exista.
 *
 * Desactivar sigue estando para lo que de verdad es: un honorario que ya no se
 * ofrece pero que se quiere seguir viendo en el tarifario.
 */
export async function deleteService(id: string): Promise<void> {
  const { error } = await supabase.rpc('delete_service', { p_service: id });
  if (error) throw error;
}
